import { Alert } from '@oncarrot/turnip';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { PageNotFound } from 'pages/PageNotFound/PageNotFound';
import { useParams, useRouteError } from 'react-router';
import { Offset } from 'turnip/Offset/Offset';
import { PageLayout } from 'turnip/PageLayout/PageLayout';
import { PageLayoutContained } from 'turnip/PageLayoutContained/PageLayoutContained';
import { PageTitle } from 'turnip/PageTitle/PageTitle';
import { PageWithAuthenticatedNavs } from 'turnip/PageWithAuthenticatedNavs/PageWithAuthenticatedNavs';
import { Stack } from 'turnip/Stack/Stack';
import { SupportLink } from 'turnip/SupportLink/SupportLink';

export function ErrorPage() {
  const error = useRouteError();
  const params = useParams();
  const singleSiteRoute = !!params.siteId;
  const Layout = singleSiteRoute ? PageLayout : PageLayoutContained;

  if (isErrorWithStatus(error) && error.status === 404) {
    return (
      <MaybeWithAuthenticatedNavs>
        <PageNotFound contained={!singleSiteRoute} />
      </MaybeWithAuthenticatedNavs>
    );
  }

  return (
    <MaybeWithAuthenticatedNavs>
      <Layout>
        <Stack gap={4}>
          <PageTitle>Something went wrong</PageTitle>

          <Alert variant="error" headline="An error occurred">
            Something went wrong while processing your request. Please try again
            or <SupportLink />.
          </Alert>
        </Stack>
      </Layout>
    </MaybeWithAuthenticatedNavs>
  );
}

function MaybeWithAuthenticatedNavs({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data } = useCurrentUser();

  if (data?.user) {
    return <PageWithAuthenticatedNavs>{children}</PageWithAuthenticatedNavs>;
  }

  return <Offset top={24}>{children}</Offset>;
}

type ErrorWithStatus = {
  status: number;
};
const isErrorWithStatus = (error: unknown): error is ErrorWithStatus =>
  typeof error === 'object' &&
  !!error &&
  'status' in error &&
  typeof error.status === 'number';
